"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui";
import Link from "next/link";
import * as React from "react";

import { Container } from "@/app/(marketing)/_components/ui/container";
import { cn } from "@/lib/utils";

import type { FAQCategory } from "./faq-data";
import FAQSidebar from "./faq-sidebar";

interface FAQContentProps {
  categories: FAQCategory[];
}

function useActiveSection(sectionIds: string[]) {
  const [activeId, setActiveId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (sectionIds.length > 0 && !activeId) {
      setActiveId(sectionIds[0] ?? null);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: "-20% 0% -60% 0%" },
    );

    for (const id of sectionIds) {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    }

    return () => {
      for (const id of sectionIds) {
        const element = document.getElementById(id);
        if (element) {
          observer.unobserve(element);
        }
      }
    };
  }, [sectionIds, activeId]);

  return activeId;
}

export default function FAQContent({ categories }: FAQContentProps) {
  const sectionIds = React.useMemo(
    () => categories.map((cat) => cat.id),
    [categories],
  );

  const activeSection = useActiveSection(sectionIds);

  return (
    <section className="bg-background py-12">
      <Container>
        <div className="grid gap-8 lg:grid-cols-[240px_1fr] lg:gap-12">
          {/* Sidebar - sticky on desktop, hidden on mobile */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <FAQSidebar
                categories={categories}
                activeCategory={activeSection}
              />
            </div>
          </aside>

          {/* Mobile category pills */}
          <div className="flex gap-2 overflow-x-auto pb-4 lg:hidden">
            {categories.map((category) => (
              <a
                key={category.id}
                href={`#${category.id}`}
                className={cn(
                  "border-border shrink-0 rounded-full border px-4 py-2 text-sm transition-colors",
                  activeSection === category.id
                    ? "bg-foreground text-background"
                    : "text-foreground hover:bg-muted",
                )}
              >
                {category.name}
              </a>
            ))}
          </div>

          {/* FAQ Sections */}
          <div className="space-y-16">
            {categories.map((category) => (
              <section
                key={category.id}
                id={category.id}
                className="scroll-mt-24"
              >
                <div className="mb-6">
                  <h2 className="text-foreground text-2xl font-semibold">
                    {category.name}
                  </h2>
                  <p className="text-muted-foreground mt-1">
                    {category.description}
                  </p>
                </div>

                <Accordion type="single" collapsible className="space-y-2">
                  {category.items.map((item) => (
                    <AccordionItem
                      key={item.id}
                      value={item.id}
                      className="group border-border bg-card data-[state=open]:ring-border rounded-xl border px-6 py-1 data-[state=open]:ring-1"
                    >
                      <AccordionTrigger className="cursor-pointer py-4 text-left text-base font-medium hover:no-underline [&>svg]:size-5">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="pt-0 pb-4">
                        <p className="text-muted-foreground leading-relaxed">
                          {item.answer}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                {/* Related links for specific categories */}
                {category.id === "security" && (
                  <p className="text-muted-foreground mt-4 text-sm">
                    Review our{" "}
                    <Link
                      href="/legal/privacy-policy"
                      className="text-foreground hover:text-brand-blue font-medium underline underline-offset-4"
                    >
                      privacy policy
                    </Link>
                    .
                  </p>
                )}
                {category.id === "enterprise" && (
                  <p className="text-muted-foreground mt-4 text-sm">
                    Ready to get started?{" "}
                    <Link
                      href="/contact"
                      className="text-foreground hover:text-brand-blue font-medium underline underline-offset-4"
                    >
                      Contact our enterprise team
                    </Link>
                    .
                  </p>
                )}
              </section>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
